import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import employeeApi from '../../lib/employeeApi';
import { useViewMode } from '../../contexts/ViewModeContext';
import { 
  LayoutDashboard, Building2, FolderKanban, Users, 
  UserCircle, Calendar, HelpCircle, LogOut, User,
  Briefcase, CheckSquare, AlertTriangle, Clock, 
  CalendarCheck, CalendarClock, Flame, Zap, RefreshCw, ListTodo, FileText, Calculator,
  ChevronDown, ChevronRight, MoreHorizontal, Award
} from 'lucide-react';
import EmployeeTasksPage from '../../components/EmployeeTasksPage';
import EmployeeClientsPage from '../../components/EmployeeClientsPage';
import EmployeeOrganisationPage from '../../components/EmployeeOrganisationPage';
import EmployeeHelpDeskPage from '../../components/EmployeeHelpDeskPage';
import CalendarsPage from '../../components/CalendarsPage';
import EmployeeLeavesPage from '../../components/EmployeeLeavesPage';
import EmployeeAccountingPage from '../../components/EmployeeAccountingPage';
import RRWinnersPage from '../../components/rr/RRWinnersPage';
import NotificationBell from '../../components/NotificationBell';
import NoticeBoard from '../../components/NoticeBoard';
import SectionErrorBoundary from '../../components/SectionErrorBoundary';
import BadgeDisplay from '../../components/BadgeDisplay';
import EmployeeProfileModal from '../../components/EmployeeProfileModal';
import { ViewModeToggleSimple } from '../../components/ViewModeToggle';
import MobileHeader from '../../components/MobileHeader';
import MobileBottomNav from '../../components/MobileBottomNav';
import MobileFAB from '../../components/MobileFAB';
import MobileSidebar from '../../components/MobileSidebar';
import { formatDateToLocal } from '../../lib/dateUtils';
import { EmployeeMetricsDashboard } from '../../components/MetricsDashboard';
// ChatWidget removed — internal messaging deprecated


// Note: axios instance previously declared here shadowed the shared
// `employeeApi` from lib/employeeApi.js — which was the root cause of the
// employee portal getting stuck at "Loading..." for anyone with a stale
// token. The shared instance ships with a retry interceptor + global 401
// handler that clears the token and bounces to /employee/login so the app
// self-heals across every browser tab.

export default function NewEmployeeDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobileView } = useViewMode();
  
  // Get initial page from URL or default to 'dashboard'
  const initialPage = searchParams.get('page') || 'dashboard';
  const [activeMenu, setActiveMenu] = useState(initialPage);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [taskStats, setTaskStats] = useState({
    myTasks: 0,
    departmentTasks: 0,
    totalTasks: 0,
    newTasks: 0,
    overdueTasks: 0,
    revisionTasks: 0,
    dueToday: 0,
    dueTomorrow: 0,
    highPriority: 0,
    criticalTasks: 0
  });
  const [taskFilter, setTaskFilter] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sync activeMenu with URL search params
  useEffect(() => {
    const page = searchParams.get('page');
    if (page && page !== activeMenu) {
      setActiveMenu(page);
    }
  }, [searchParams]);

  // Update URL when activeMenu changes
  const updateUrlWithMenu = (menu) => {
    if (menu === 'dashboard') {
      searchParams.delete('page');
    } else {
      searchParams.set('page', menu);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Handle menu change with URL sync
  const handleMenuChange = (key) => {
    setActiveMenu(key);
    updateUrlWithMenu(key);
    setTaskFilter(null);
  };

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  // Safety net: if for ANY reason we're still spinning after 15 s, assume the
  // session is broken and bounce to login. Prevents the "stuck forever"
  // loading state that used to trap non-incognito browsers with stale tokens.
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      if (loading && !employee) {
        try {
          localStorage.removeItem('employee_token');
          localStorage.removeItem('employee_user');
        } catch {}
        toast.error('Session expired. Please sign in again.');
        navigate('/employee/login', { replace: true });
      }
    }, 15000);
    return () => clearTimeout(t);
  }, [loading, employee, navigate]);

  const fetchEmployeeData = async () => {
    try {
      // Fetch employee profile and tasks with server-side stats
      const [meRes, tasksRes] = await Promise.all([
        employeeApi.get('/api/v1/auth/me'),
        employeeApi.get('/api/v1/tasks', { params: { view_mode: 'my', page_size: 50 } })
      ]);

      setEmployee(meRes.data);
      
      // Use server-side stats for accurate counts from ALL data
      const stats = tasksRes.data.stats || {};
      
      // Also get department and all tasks stats for comparison metrics
      let deptStats = { pending: 0 };
      let allStats = { pending: 0 };
      
      try {
        const [deptRes, allRes] = await Promise.all([
          employeeApi.get('/api/v1/tasks', { params: { view_mode: 'department', page_size: 1 } }),
          employeeApi.get('/api/v1/tasks', { params: { view_mode: 'all', page_size: 1 } })
        ]);
        deptStats = deptRes.data.stats || {};
        allStats = allRes.data.stats || {};
      } catch (e) {
        // Silently handle - department/all stats are optional
      }
      
      setTaskStats({
        myTasks: stats.my_pending || stats.pending || 0,  // My pending tasks
        departmentTasks: deptStats.pending || 0,
        totalTasks: allStats.pending || 0,
        newTasks: stats.new || 0,
        overdueTasks: stats.overdue || 0,
        revisionTasks: stats.revision || 0,
        dueToday: stats.today || 0,
        dueTomorrow: stats.tomorrow || 0,
        highPriority: stats.high || 0,
        criticalTasks: stats.critical || 0
      });
    } catch (error) {
      console.error('Error fetching employee data:', error);
      // A 401 has ALREADY been handled by the shared employeeApi interceptor
      // (clears token + navigates to /employee/login). For any other class of
      // failure — network, 5xx, timeout — try the legacy endpoints once, then
      // give up cleanly so the UI can render instead of spinning.
      const status = error?.response?.status;
      if (status === 401) return; // interceptor is already redirecting
      try {
        const [meRes, myTasksRes, deptTasksRes, allTasksRes] = await Promise.all([
          employeeApi.get('/api/employee/auth/me'),
          employeeApi.get('/api/employee/tasks?view_mode=my'),
          employeeApi.get('/api/employee/tasks?view_mode=department'),
          employeeApi.get('/api/employee/tasks?view_mode=all')
        ]);

        setEmployee(meRes.data);
        const myTasks = myTasksRes.data || [];
        const deptTasks = deptTasksRes.data || [];
        const allTasks = allTasksRes.data || [];
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        const formatDate = (d) => formatDateToLocal(d);

        setTaskStats({
          myTasks: myTasks.filter(t => t.status !== 'Completed').length,
          departmentTasks: deptTasks.filter(t => t.status !== 'Completed').length,
          totalTasks: allTasks.filter(t => t.status !== 'Completed').length,
          newTasks: allTasks.filter(t => t.status === 'New').length,
          overdueTasks: allTasks.filter(t => {
            if (!t.due_date || t.status === 'Completed') return false;
            const taskDate = new Date(t.due_date + 'T00:00:00');
            return taskDate < today;
          }).length,
          revisionTasks: allTasks.filter(t => t.status === 'Revision').length,
          dueToday: allTasks.filter(t => t.due_date === formatDate(today) && t.status !== 'Completed').length,
          dueTomorrow: allTasks.filter(t => t.due_date === formatDate(tomorrow) && t.status !== 'Completed').length,
          highPriority: allTasks.filter(t => t.priority === 'High' && t.status !== 'Completed').length,
          criticalTasks: allTasks.filter(t => t.priority === 'Critical' && t.status !== 'Completed').length
        });
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        const fbStatus = fallbackError?.response?.status;
        if (fbStatus === 401) return; // interceptor handles
        // Do NOT bounce the user out just because the network hiccupped —
        // render the shell with a toast so they can retry manually.
        toast.error('Could not load your dashboard. Please refresh or try again in a moment.');
      }
    } finally {
      // ALWAYS drop the loading gate — this is the fix for the "stuck at
      // loading" symptom users hit outside incognito.
      setLoading(false);
    }
  };

  // REMOVED: isBossDepartment() - Boss department privileges have been eliminated
  // All employees now have uniform access regardless of department

  // Main menu items (always visible)
  const mainMenuItems = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'tasks', label: 'Tasks', icon: CheckSquare },
    { key: 'calendars', label: 'Calendars', icon: Calendar },
    { key: 'organisation', label: 'Organisation', icon: Users },
    { key: 'clients', label: 'Clients', icon: Building2 },
  ];

  // More menu items - UNIFORM for all employees (no department-based privileges)
  const getMoreMenuItems = () => {
    const items = [
      { key: 'leaves', label: 'Leave Management', icon: FileText },
      { key: 'accounting', label: 'Accounting', icon: Calculator },
      { key: 'rewards', label: 'Rewards & Winners', icon: Award },
      { key: 'helpdesk', label: 'Help Desk', icon: HelpCircle },
    ];
    
    return items;
  };
  
  const moreMenuItems = getMoreMenuItems();

  // Combined for backwards compatibility
  const menuItems = [...mainMenuItems, ...moreMenuItems];

  // State for More section expansion
  const [isMoreExpanded, setIsMoreExpanded] = useState(false);

  // Auto-expand More section if a "more" menu item is active
  useEffect(() => {
    if (moreMenuItems.some(item => item.key === activeMenu)) {
      setIsMoreExpanded(true);
    }
  }, [activeMenu]);

  const handleLogout = () => {
    localStorage.removeItem('employee_token');
    localStorage.removeItem('employee_user');
    navigate('/employee/login');
    toast.success('Logged out successfully');
  };

  // Navigate to tasks with filter
  const handleStatClick = (filterType) => {
    setTaskFilter(filterType);
    setActiveMenu('tasks');
    updateUrlWithMenu('tasks');
  };

  // Get menu title for mobile header
  const getMenuTitle = () => {
    const item = menuItems.find(m => m.key === activeMenu);
    return item?.label || 'Dashboard';
  };

  // Dashboard Stats Configuration - 3 strategic sections
  const myWorkloadStats = [
    { key: 'myTasks', label: 'Assigned to Me', value: taskStats.myTasks, icon: Briefcase, color: 'amber', filter: 'my' },
    { key: 'overdueTasks', label: 'Overdue', value: taskStats.myOverdue ?? taskStats.overdueTasks, icon: AlertTriangle, color: 'red', filter: 'overdue' },
    { key: 'dueToday', label: 'Due Today', value: taskStats.dueToday, icon: CalendarCheck, color: 'emerald', filter: 'today' },
    { key: 'dueTomorrow', label: 'Due Tomorrow', value: taskStats.dueTomorrow, icon: CalendarClock, color: 'teal', filter: 'tomorrow' },
  ];

  const createdByMeStats = [
    { key: 'createdByMe', label: 'Created by Me', value: taskStats.createdByMe, icon: ListTodo, color: 'purple', filter: 'created' },
    { key: 'createdAwaiting', label: 'Awaiting Completion', value: taskStats.createdAwaitingCompletion, icon: Clock, color: 'cyan', filter: 'created' },
    { key: 'createdAttention', label: 'Needs Attention', value: taskStats.createdNeedsAttention, icon: RefreshCw, color: 'orange', filter: 'created' },
    { key: 'createdCompleted', label: 'Completed', value: taskStats.createdCompleted, icon: Zap, color: 'fuchsia', filter: 'created' },
  ];

  const contextStats = [
    { key: 'departmentTasks', label: 'Department Active', value: taskStats.departmentTasks, icon: FolderKanban, color: 'blue', filter: 'department' },
    { key: 'newTasks', label: 'New Tasks', value: taskStats.newTasks, icon: Clock, color: 'cyan', filter: 'new' },
    { key: 'highPriority', label: 'High Priority', value: taskStats.highPriority, icon: Flame, color: 'rose', filter: 'high' },
    { key: 'criticalTasks', label: 'Critical', value: taskStats.criticalTasks, icon: Zap, color: 'fuchsia', filter: 'critical' },
  ];

  // Flat list for backward compat (mobile carousel etc)
  const dashboardStats = [...myWorkloadStats, ...createdByMeStats, ...contextStats];

  const colorClasses = {
    amber: { bg: 'bg-amber-50', border: 'border-amber-100', iconBg: 'bg-amber-100', iconText: 'text-amber-600', text: 'text-amber-600', gradient: 'from-amber-400 to-amber-500' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-100', iconBg: 'bg-purple-100', iconText: 'text-purple-600', text: 'text-purple-600', gradient: 'from-purple-500 to-purple-600' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-100', iconBg: 'bg-blue-100', iconText: 'text-blue-600', text: 'text-blue-600', gradient: 'from-[#3366FF] to-[#3366FF]' },
    cyan: { bg: 'bg-cyan-50', border: 'border-cyan-100', iconBg: 'bg-cyan-100', iconText: 'text-cyan-600', text: 'text-cyan-600', gradient: 'from-cyan-500 to-cyan-600' },
    red: { bg: 'bg-red-50', border: 'border-red-100', iconBg: 'bg-red-100', iconText: 'text-red-600', text: 'text-red-600', gradient: 'from-red-500 to-red-600' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-100', iconBg: 'bg-orange-100', iconText: 'text-orange-600', text: 'text-orange-600', gradient: 'from-orange-500 to-orange-600' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600', text: 'text-emerald-600', gradient: 'from-emerald-500 to-emerald-600' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-100', iconBg: 'bg-teal-100', iconText: 'text-teal-600', text: 'text-teal-600', gradient: 'from-teal-500 to-teal-600' },
    rose: { bg: 'bg-rose-50', border: 'border-rose-100', iconBg: 'bg-rose-100', iconText: 'text-rose-600', text: 'text-rose-600', gradient: 'from-rose-500 to-rose-600' },
    fuchsia: { bg: 'bg-fuchsia-50', border: 'border-fuchsia-100', iconBg: 'bg-fuchsia-100', iconText: 'text-fuchsia-600', text: 'text-fuchsia-600', gradient: 'from-fuchsia-500 to-fuchsia-600' },
    violet: { bg: 'bg-violet-50', border: 'border-violet-100', iconBg: 'bg-violet-100', iconText: 'text-violet-600', text: 'text-violet-600', gradient: 'from-violet-500 to-violet-600' }
  };

  // ============================================
  // PROFILE HYDRATION GATE — block render until `/auth/me` responds so
  // employees never see sections they aren't allowed to see even for a
  // split-second on reload.
  // ============================================
  if (!employee) {
    return (
      <div className="min-h-screen bg-[#F7F6F9] dark:bg-[#111111] flex items-center justify-center" data-testid="employee-dashboard-role-gate">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-teal-300 border-t-teal-600 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-neutral-500">Loading workspace…</p>
        </div>
      </div>
    );
  }

  // ============================================
  // MOBILE VIEW
  // ============================================
  if (isMobileView) {
    return (
      <div className="min-h-screen bg-[#F7F6F9] dark:bg-[#111111]" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }} data-testid="employee-dashboard-mobile">
        {/* Mobile Header */}
        <MobileHeader
          onMenuOpen={() => setIsSidebarOpen(true)}
          api={employeeApi}
          portalType="employee"
          title={getMenuTitle()}
        />

        {/* Mobile Sidebar */}
        <MobileSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          menuItems={mainMenuItems}
          moreMenuItems={moreMenuItems}
          activeMenu={activeMenu}
          setActiveMenu={handleMenuChange}
          onLogout={handleLogout}
          userName={employee?.personal?.full_name || 'Employee'}
          userRole={employee?.employment?.designation || 'Team Member'}
          portalType="employee"
        />

        {/* Main Content - Mobile */}
        <main className="px-3 py-3">
          {/* Dashboard Content - Mobile - SAME DATA AS DESKTOP */}
          {activeMenu === 'dashboard' && (
            <div data-testid="employee-dashboard-content-mobile">
              {/* Welcome with Badges - Mobile */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-lg font-bold text-[#1a1a2e]">Dashboard</h1>
                    <p className="text-xs text-neutral-500">Welcome back, {employee?.personal?.full_name?.split(' ')[0] || 'there'}!</p>
                  </div>
                  <BadgeDisplay 
                    employee={employee} 
                    size="small" 
                    showLabels={false}
                    layout="horizontal"
                  />
                </div>
              </div>

              <div className="mb-4">
                <SectionErrorBoundary label="Metrics">
                  <EmployeeMetricsDashboard 
                    api={employeeApi} 
                    onTaskFilter={(filter) => {
                      setTaskFilter(filter);
                      handleMenuChange('tasks');
                    }}
                  />
                </SectionErrorBoundary>
              </div>

              {/* Quick Actions - Mobile Glassy */}
              <div className="glass-card p-4 mb-4">
                <h2 className="text-sm font-semibold text-[#1a1a2e] mb-3">Quick Actions</h2>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => handleMenuChange('tasks')}
                    className="p-4 bg-white/60 border border-white/40 rounded-2xl hover:bg-white/80 transition-all text-center">
                    <CheckSquare className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#1a1a2e]">View Tasks</p>
                  </button>
                  <button onClick={() => handleMenuChange('clients')}
                    className="p-4 bg-white/60 border border-white/40 rounded-2xl hover:bg-white/80 transition-all text-center">
                    <Building2 className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#1a1a2e]">View Clients</p>
                  </button>
                  <button onClick={() => handleMenuChange('organisation')}
                    className="p-4 bg-white/60 border border-white/40 rounded-2xl hover:bg-white/80 transition-all text-center">
                    <Users className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#1a1a2e]">View Team</p>
                  </button>
                  <button onClick={() => handleMenuChange('helpdesk')}
                    className="p-4 bg-white/60 border border-white/40 rounded-2xl hover:bg-white/80 transition-all text-center">
                    <HelpCircle className="w-6 h-6 text-pink-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#1a1a2e]">Get Help</p>
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <SectionErrorBoundary label="Notice Board">
                  <NoticeBoard role="employee" />
                </SectionErrorBoundary>
              </div>
            </div>
          )}

          {/* Other Pages */}
          {activeMenu === 'tasks' && <EmployeeTasksPage initialFilter={taskFilter} onClearFilter={() => setTaskFilter(null)} />}
          {activeMenu === 'leaves' && <EmployeeLeavesPage />}
          {activeMenu === 'calendars' && <SectionErrorBoundary label="Calendars"><CalendarsPage api={employeeApi} userType="employee" /></SectionErrorBoundary>}
          {activeMenu === 'accounting' && <EmployeeAccountingPage />}
          {activeMenu === 'clients' && <EmployeeClientsPage />}
          {activeMenu === 'organisation' && <EmployeeOrganisationPage />}
          {activeMenu === 'helpdesk' && <EmployeeHelpDeskPage />}
          {activeMenu === 'rewards' && <SectionErrorBoundary label="R&R Winners"><RRWinnersPage api={employeeApi} /></SectionErrorBoundary>}
        </main>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNav
          activeMenu={activeMenu}
          setActiveMenu={handleMenuChange}
          onMenuOpen={() => setIsSidebarOpen(true)}
          portalType="employee"
        />

        {/* Contextual Floating Action Button */}
        <MobileFAB
          activeMenu={activeMenu}
          portalType="employee"
          onAction={(action) => {
            const menuMap = {
              'new-task': 'tasks',
              'check-in': 'attendance',
              'new-ticket': 'helpdesk',
            };
            const target = menuMap[action];
            if (target && target !== activeMenu) handleMenuChange(target);
            window.dispatchEvent(new CustomEvent('girgit:mobile-fab', { detail: { action } }));
          }}
        />
      </div>
    );
  }

  // ============================================
  // DESKTOP VIEW
  // ============================================
  return (
    <div className="min-h-screen bg-[#F7F6F9] dark:bg-[#111111] flex" data-testid="employee-dashboard-desktop">
      {/* Sidebar — Charcoal + Amber accents */}
      <aside className="w-[260px] bg-[#111111] flex flex-col fixed inset-y-0 left-0 z-20 rounded-r-[28px] shadow-2xl">
        <div className="p-5 pt-7">
          <div className="flex items-center gap-2.5">
            <img src="/hed-icon.png" alt="HED" className="w-7 h-7 rounded-lg" />
            <div className="w-px h-5 bg-white/10" />
            <img src="/girgit-logo.png" alt="Girgit" className="w-7 h-7 rounded-lg" />
            <span className="text-white font-bold text-base tracking-tight">Girgit</span>
          </div>
        </div>

        <div className="px-4 py-3">
          <button onClick={() => setShowProfileModal(true)}
            className="flex items-center gap-3 w-full bg-white/[0.06] rounded-2xl p-3 border border-white/[0.05] hover:bg-white/[0.08] transition-colors"
            data-testid="user-profile-btn">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="font-semibold text-sm text-white truncate">{employee?.personal?.full_name || 'Employee'}</p>
              <p className="text-[11px] text-white/40">{employee?.employment?.designation || 'Team Member'}</p>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-white/20" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-1 overflow-y-auto scrollbar-thin">
          {mainMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeMenu === item.key;
            return (
              <button key={item.key} onClick={() => handleMenuChange(item.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl mb-0.5 transition-all duration-200 ${
                  isActive 
                    ? 'bg-amber-500/15 text-amber-400 font-semibold' 
                    : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                }`}
                data-testid={`desktop-menu-${item.key}`}>
                <Icon className="w-4 h-4" /><span className="text-[13px]">{item.label}</span>
              </button>
            );
          })}

          <div className="mt-2 pt-2 border-t border-white/[0.06]">
            <button onClick={() => setIsMoreExpanded(!isMoreExpanded)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-white/30 hover:bg-white/[0.06] hover:text-white/60 rounded-2xl transition-all"
              data-testid="desktop-more-toggle">
              <div className="flex items-center gap-2.5">
                <MoreHorizontal className="w-4 h-4" />
                <span className="text-[11px] font-semibold tracking-wider">More</span>
              </div>
              <ChevronRight className={`w-3 h-3 transition-transform duration-300 ${isMoreExpanded ? 'rotate-90' : ''}`} />
            </button>

            {isMoreExpanded && (
              <div className="pl-1 space-y-0.5 mt-0.5">
                {moreMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeMenu === item.key;
                  return (
                    <button key={item.key} onClick={() => handleMenuChange(item.key)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl transition-all duration-200 ${
                        isActive 
                          ? 'bg-amber-500/15 text-amber-400 font-semibold' 
                          : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                      }`}
                      data-testid={`desktop-menu-${item.key}`}>
                      <Icon className="w-4 h-4" /><span className="text-[13px]">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </nav>

        <div className="p-4 border-t border-white/[0.06]">
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-white/40 hover:bg-white/[0.06] hover:text-[#FF6B8A] rounded-2xl transition-all duration-200"
            data-testid="desktop-logout-btn">
            <LogOut className="w-4 h-4" /><span className="text-[13px]">Logout</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-[260px] overflow-y-auto min-h-screen">
        <header className="sticky top-0 z-10 bg-white/60 dark:bg-[#111111]/80 backdrop-blur-2xl border-b border-white/40 dark:border-white/[0.06] px-8 py-4 flex items-center justify-end gap-3">
          <ViewModeToggleSimple />
          <NotificationBell api={employeeApi} portalType="employee" accentColor="yellow" />
        </header>

        {activeMenu === 'dashboard' && (
          <div className="p-8" data-testid="employee-dashboard-content-desktop">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-[#1a1a2e] tracking-tight">Dashboard</h1>
                <p className="text-sm text-neutral-500 mt-1">
                  Welcome back, 
                  <button onClick={() => setShowProfileModal(true)} className="text-[#FF6B8A] font-semibold ml-1 underline decoration-2 underline-offset-4">
                    {employee?.personal?.full_name?.split(' ')[0] || 'there'}
                  </button>!
                </p>
              </div>
              <BadgeDisplay employee={employee} size="medium" showLabels={true} layout="horizontal" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <SectionErrorBoundary label="Metrics">
                  <EmployeeMetricsDashboard api={employeeApi} onTaskFilter={(filter) => { setTaskFilter(filter); handleMenuChange('tasks'); }} />
                </SectionErrorBoundary>

                {/* Quick Actions — Glassy */}
                <div className="glass-card p-6">
                  <h2 className="text-sm font-semibold text-[#1a1a2e] mb-5">Quick Actions</h2>
                  <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                    <button onClick={() => handleMenuChange('tasks')} className="p-5 bg-white/60 backdrop-blur-sm border border-white/40 rounded-2xl hover:bg-white/80 hover:shadow-soft hover:-translate-y-0.5 transition-all duration-200 text-center group">
                      <CheckSquare className="w-6 h-6 text-amber-500 mx-auto mb-2.5 group-hover:scale-110 transition-transform" />
                      <p className="text-xs font-semibold text-[#1a1a2e]">Tasks</p>
                    </button>
                    <button onClick={() => handleMenuChange('clients')} className="p-5 bg-white/60 backdrop-blur-sm border border-white/40 rounded-2xl hover:bg-white/80 hover:shadow-soft hover:-translate-y-0.5 transition-all duration-200 text-center group">
                      <Building2 className="w-6 h-6 text-blue-500 mx-auto mb-2.5 group-hover:scale-110 transition-transform" />
                      <p className="text-xs font-semibold text-[#1a1a2e]">Clients</p>
                    </button>
                    <button onClick={() => handleMenuChange('organisation')} className="p-5 bg-white/60 backdrop-blur-sm border border-white/40 rounded-2xl hover:bg-white/80 hover:shadow-soft hover:-translate-y-0.5 transition-all duration-200 text-center group">
                      <Users className="w-6 h-6 text-purple-500 mx-auto mb-2.5 group-hover:scale-110 transition-transform" />
                      <p className="text-xs font-semibold text-[#1a1a2e]">Team</p>
                    </button>
                    <button onClick={() => handleMenuChange('helpdesk')} className="p-5 bg-white/60 backdrop-blur-sm border border-white/40 rounded-2xl hover:bg-white/80 hover:shadow-soft hover:-translate-y-0.5 transition-all duration-200 text-center group">
                      <HelpCircle className="w-6 h-6 text-pink-500 mx-auto mb-2.5 group-hover:scale-110 transition-transform" />
                      <p className="text-xs font-semibold text-[#1a1a2e]">Help</p>
                    </button>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <SectionErrorBoundary label="Notice Board">
                  <NoticeBoard role="employee" />
                </SectionErrorBoundary>
              </div>
            </div>
          </div>
        )}

        {/* Tasks Page */}
        {activeMenu === 'tasks' && <EmployeeTasksPage initialFilter={taskFilter} onClearFilter={() => setTaskFilter(null)} />}

        {/* Leaves Page */}
        {activeMenu === 'leaves' && <EmployeeLeavesPage />}

        {/* Calendars Page */}
        {activeMenu === 'calendars' && <SectionErrorBoundary label="Calendars"><CalendarsPage api={employeeApi} userType="employee" /></SectionErrorBoundary>}

        {/* Accounting Page */}
        {activeMenu === 'accounting' && <EmployeeAccountingPage />}

        {/* Clients Page */}
        {activeMenu === 'clients' && <EmployeeClientsPage />}

        {/* Organisation Page */}
        {activeMenu === 'organisation' && <EmployeeOrganisationPage />}

        {/* Rewards & Recognition Nominations */}
        {activeMenu === 'rewards' && <SectionErrorBoundary label="R&R Winners"><RRWinnersPage api={employeeApi} /></SectionErrorBoundary>}

        {/* Help Desk Page */}
        {activeMenu === 'helpdesk' && <EmployeeHelpDeskPage />}


        {/* Other Menu Placeholders */}
        {!['dashboard', 'tasks', 'leaves', 'calendars', 'accounting', 'clients', 'organisation', 'helpdesk', 'rewards'].includes(activeMenu) && (
          <div className="p-8">
            <h1 className="text-2xl font-bold text-[#1a1a2e] capitalize">{activeMenu}</h1>
            <div className="mt-8 glass-card p-12 text-center">
              <p className="text-neutral-500">Full implementation coming soon...</p>
            </div>
          </div>
        )}
      </main>

      {/* Employee Profile Modal */}
      <EmployeeProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        employee={employee}
        api={employeeApi}
        onUpdate={fetchEmployeeData}
      />
    </div>
  );
}
